import { reactive, watch } from "vue";
import { createChats, type Chat, type Message } from "../data/demo";
import { MAX_PINNED_CHATS } from "../config";
import { normalizeReactions } from "./reactions";
const STORAGE = "wa-interface-lab-v1";
const defaults = {
  active: "c0",
  wallpaper: "Padrão",
  notifications: true,
  sound: true,
  enterSend: true,
  spellcheck: true,
  lists: [] as string[],
};
export function restore(raw: string | null): {
  chats: Chat[];
  name: string;
  about: string;
  theme: string;
} & typeof defaults {
  try {
    const v = JSON.parse(raw || "null");
    if (
      v &&
      Array.isArray(v.chats) &&
      v.chats.length &&
      v.chats.every(
        (c: any) =>
          typeof c.id === "string" &&
          typeof c.name === "string" &&
          Array.isArray(c.messages),
      )
    ) {
      return {
        ...defaults,
        chats: v.chats.map((chat: Chat) => ({
          ...chat,
          messages: chat.messages.map((message) => ({
            ...message,
            attending:
              message.attending ||
              (message.kind === "event" &&
                (message.reactions as unknown[])?.includes("Vou participar")),
            reactions: normalizeReactions(message.reactions),
          })),
        })),
        name: typeof v.name === "string" ? v.name : "Leo",
        about: typeof v.about === "string" ? v.about : "Disponível",
        theme: v.theme === "dark" ? "dark" : "light",
        active:
          v.active === "" || v.chats.some((c: Chat) => c.id === v.active)
            ? v.active
            : v.chats[0].id,
        wallpaper:
          typeof v.wallpaper === "string" ? v.wallpaper : defaults.wallpaper,
        notifications:
          typeof v.notifications === "boolean" ? v.notifications : true,
        sound: typeof v.sound === "boolean" ? v.sound : true,
        enterSend: typeof v.enterSend === "boolean" ? v.enterSend : true,
        spellcheck: typeof v.spellcheck === "boolean" ? v.spellcheck : true,
        lists: Array.isArray(v.lists)
          ? v.lists.filter((x: unknown) => typeof x === "string")
          : [],
      };
    }
  } catch {}
  return {
    ...defaults,
    lists: [],
    chats: createChats(),
    name: "Leo",
    about: "Disponível",
    theme: "light",
  };
}
let raw: string | null = null;
try {
  if (typeof localStorage !== "undefined") raw = localStorage.getItem(STORAGE);
} catch {}
export const state = reactive({
  ...restore(raw),
});
export function persist() {
  try {
    const data = {
      ...state,
      chats: state.chats.map((c) => ({
        ...c,
        messages: c.messages.filter((m) => !m.src?.startsWith("blob:")),
      })),
    };
    localStorage.setItem(STORAGE, JSON.stringify(data));
  } catch {}
}
if (typeof window !== "undefined") watch(state, persist, { deep: true });
export function addMessage(
  chatId: string,
  input: Partial<Message> & Pick<Message, "text">,
) {
  const chat = state.chats.find((c) => c.id === chatId);
  if (!chat) return;
  const msg: Message = {
    id: crypto.randomUUID(),
    out: true,
    date: new Date().toLocaleDateString("sv-SE"),
    time: new Date().toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    }),
    status: "pending",
    ...input,
  };
  chat.messages.push(msg);
  chat.preview = msg.text;
  chat.time = msg.time;
  // Only new demo sends advance automatically; production receipts come from the API.
  if (
    msg.out &&
    !input.status &&
    !msg.queued &&
    !msg.failed &&
    msg.uploading === undefined
  ) {
    const steps = [
      [600, "pending", "sent"],
      [1500, "sent", "delivered"],
    ] as const;
    for (const [delay, previous, next] of steps) {
      setTimeout(() => {
        const m = state.chats
          .find((c) => c.id === chatId)
          ?.messages.find((x) => x.id === msg.id);
        if (
          m &&
          !m.deleted &&
          !m.failed &&
          !m.queued &&
          m.uploading === undefined &&
          m.status === previous
        )
          m.status = next;
      }, delay);
    }
  }
  return msg;
}
export function removeMessage(
  chatId: string,
  messageId: string,
  everyone: boolean,
) {
  const chat = state.chats.find((c) => c.id === chatId);
  if (!chat) return;
  const index = chat.messages.findIndex((m) => m.id === messageId);
  if (index < 0) return;
  const original: Message = JSON.parse(JSON.stringify(chat.messages[index]));
  if (everyone)
    chat.messages[index] = {
      id: original.id,
      text: "Mensagem apagada",
      out: original.out,
      time: original.time,
      date: original.date,
      deleted: true,
    };
  else chat.messages.splice(index, 1);
  chat.preview = chat.messages.at(-1)?.text || "Nenhuma mensagem";
  return () => {
    if (everyone) {
      const current = chat.messages.findIndex((m) => m.id === messageId);
      if (current >= 0) chat.messages[current] = original;
    } else chat.messages.splice(index, 0, original);
    chat.preview = chat.messages.at(-1)?.text || "Nenhuma mensagem";
  };
}
export function vote(message: Message, index: number) {
  if (!message.options || index < 0 || index >= message.options.length) return;
  message.votes ??= message.options?.map(() => 0) || [];
  message.selected ??= [];
  if (message.selected.includes(index)) {
    message.votes[index] = Math.max(0, message.votes[index] - 1);
    message.selected = message.selected.filter((i) => i !== index);
  } else {
    if (!message.multiple) {
      for (const i of message.selected)
        message.votes[i] = Math.max(0, message.votes[i] - 1);
      message.selected = [];
    }
    message.selected.push(index);
    message.votes[index]++;
  }
}
export function toggleChatPin(chatId: string, limit = MAX_PINNED_CHATS) {
  const chat = state.chats.find((c) => c.id === chatId);
  if (!chat) return "missing";
  if (chat.pinned) {
    chat.pinned = false;
    delete chat.pinnedAt;
    return "unpinned";
  }
  const pinned = state.chats.filter((c) => c.pinned);
  if (pinned.length >= limit) return "limit";
  // Keep ordering deterministic even for clicks in the same millisecond.
  chat.pinnedAt = Math.max(
    Date.now(),
    Math.max(0, ...pinned.map((c) => c.pinnedAt ?? 0)) + 1,
  );
  chat.pinned = true;
  return "pinned";
}
export function filteredChats(chats: Chat[], search: string, filter: string) {
  const q = search
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  return chats
    .filter(
      (c) =>
        (filter === "Arquivadas" ? c.archived : !c.archived) &&
        c.name
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
          .includes(q) &&
        (filter !== "Não lidas" || c.unread > 0) &&
        (filter !== "Favoritas" || c.favorite) &&
        (filter !== "Grupos" || c.group) &&
        (["Tudo", "Não lidas", "Favoritas", "Grupos", "Arquivadas"].includes(
          filter,
        ) ||
          !!c.lists?.includes(filter)),
    )
    .sort(
      (a, b) =>
        Number(!!b.pinned) - Number(!!a.pinned) ||
        (a.pinned && b.pinned ? (b.pinnedAt ?? 0) - (a.pinnedAt ?? 0) : 0),
    );
}
export function resetDemo() {
  Object.assign(state, { ...restore(null), active: "c0" });
}
